import { DeviceType } from '@/app/components/DeviceSelectionGallery/DeviceSelectionGallery.types'
import useDeviceEnrollments from '@/hooks/useDeviceEnrollments/useDeviceEnrollments'
import {
  ChargerEnrollment,
  BehavioralEnrollment,
  ThermostatEnrollment,
  VehicleEnrollment,
} from '@/types/programs-new/programs-new'
import { match } from 'ts-pattern'

export type Props = {
  deviceId: number
  deviceType: Exclude<DeviceType, 'energySystem'>
}

type DeviceEnrollment =
  | VehicleEnrollment
  | ThermostatEnrollment
  | ChargerEnrollment

function findEnrollmentForDevice(
  deviceId: number,
  enrollments: DeviceEnrollment[],
  deviceType: DeviceType,
): DeviceEnrollment | undefined {
  return enrollments.find((enrollment) =>
    match(deviceType)
      .with(
        'vehicle',
        () => 'vehicle_id' in enrollment && enrollment.vehicle_id === deviceId,
      )
      .with(
        'thermostat',
        () =>
          'thermostat_id' in enrollment &&
          enrollment.thermostat_id === deviceId,
      )
      .with(
        'vehicleCharger',
        () => 'charger_id' in enrollment && enrollment.charger_id === deviceId,
      )
      .otherwise(() => false),
  )
}

function getAlertContent(
  enrollment: DeviceEnrollment | undefined,
  deviceType: DeviceType,
): string {
  if (!enrollment?.view_config_json?.dashboard) return ''
  if (!enrollment.enrolled) return ''

  return match(deviceType)
    .with(
      'vehicle',
      () => enrollment.view_config_json?.dashboard?.alertHeaderContent ?? '',
    )
    .with(
      'thermostat',
      () =>
        enrollment.view_config_json?.dashboard?.thermostatAlertHeaderContent ??
        '',
    )
    .with(
      'vehicleCharger',
      () =>
        enrollment.view_config_json?.dashboard?.chargerAlertHeaderContent ?? '',
    )
    .otherwise(() => '')
}

export function useViewModel(props: Props) {
  const { deviceEnrollments } = useDeviceEnrollments(
    props.deviceType,
    props.deviceId,
  )

  // Extract and filter enrollments
  const validEnrollments = deviceEnrollments
    .map((e) => e.enrollment)
    .filter((e): e is DeviceEnrollment => e !== undefined)

  // Find the specific enrollment for this device
  const deviceEnrollment = findEnrollmentForDevice(
    props.deviceId,
    validEnrollments,
    props.deviceType,
  )

  // Get the alert content for this device type
  const dashboardAlertContentRaw = getAlertContent(
    deviceEnrollment,
    props.deviceType,
  )

  // Replace placeholder with actual device ID
  const dashboardAlertContent = dashboardAlertContentRaw.replace(
    '${deviceId}',
    props.deviceId.toString(),
  )

  return {
    dashboardAlertContent,
  }
}
